import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    IconButton, 
    Paper, 
    List, 
    ListItem, 
    Avatar,
    CircularProgress,
    Alert,
    InputAdornment
} from '@mui/material';
import { 
    Send as SendIcon, 
    ArrowBack as ArrowBackIcon,
    Mic as MicIcon,
    SmartToy as RobotIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';
import { format } from 'date-fns';
import { useMessages } from '../../context/MessageContext';

import { useAuth } from '../../context/AuthContext';
import { parseUtc } from '../../utils/time';


const ConversationView = () => {
    const { channelType, participantId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const jobId = queryParams.get('job_id');
    const { markAsRead, fetchConversations } = useMessages();
    const { user, effectiveRole } = useAuth();
    const isWorker = (effectiveRole || user?.role) === 'worker';

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const url = `/messages/conversation/${channelType}/${participantId}${jobId ? `?job_id=${jobId}` : ''}`;
                const response = await api.get(url);
                const newMessages = response.data;
                setMessages(newMessages);
                
                // Mark unread messages as read
                const unreadMessages = newMessages.filter(m => !m.read_at && m.recipient_id !== null); // Simplified check
                if (unreadMessages.length > 0) {
                    await Promise.all(unreadMessages.map(m => markAsRead(m.id)));
                }
            } catch (err) {
                console.error('Failed to fetch messages:', err);
                setError('Failed to load conversation history.');
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
        
        // Setup polling for new messages (real-time replacement)
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, [channelType, participantId, jobId, markAsRead]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!newMessage.trim() || sending) return;

        setSending(true);
        try {
            const recipientType =
                channelType === 'manager_ai_private' ? 'ai' :
                channelType === 'manager_worker' || channelType === 'worker_ai' ? 'worker' :
                'client';
            const parsedParticipantId = parseInt(participantId, 10);
            const recipientId = Number.isNaN(parsedParticipantId) ? null : parsedParticipantId;
            
            const response = await api.post('/messages/send', {
                channel_type: channelType,
                recipient_id: participantId === 'ai' || participantId === 'self' ? null : recipientId,
                recipient_type: recipientType,
                content: newMessage,
                job_id: jobId ? parseInt(jobId) : null
            });

            setMessages([...messages, response.data]);
            setNewMessage('');
            fetchConversations(); // Update global state
        } catch (err) {
            console.error('Failed to send message:', err);
            setError('Failed to send message.');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', bgcolor: '#F5F5F5' }}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 1.5, display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 0 }}>
                <IconButton onClick={() => navigate(isWorker ? '/worker/messages' : '/messages')} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Avatar sx={{ bgcolor: channelType.includes('ai') ? '#3B82F6' : '#10B981', mr: 2 }}>
                    {channelType.includes('ai') ? <RobotIcon /> : <PersonIcon />}
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {channelType === 'manager_ai_private' ? 'AI Assistant (Private)' : 
                         channelType === 'worker_ai' ? 'Worker ↔ AI' :
                         channelType === 'client_ai' ? 'Client ↔ AI' : 'Direct Message'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {channelType === 'client_ai' && jobId ? `Job #${jobId}` : 'Active now'}
                    </Typography>
                </Box>
            </Paper>

            {/* Messages Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {messages.map((msg) => {
                    const isOwn = msg.sender_type !== 'ai' && msg.sender_type !== 'client'; // Simplified logic
                    return (
                        <Box 
                            key={msg.id} 
                            sx={{ 
                                alignSelf: isOwn ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isOwn ? 'flex-end' : 'flex-start'
                            }}
                        >
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2,
                                    bgcolor: isOwn ? '#3B82F6' : 'white',
                                    color: isOwn ? 'white' : 'text.primary',
                                    border: isOwn ? 'none' : '1px solid #E2E8F0',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <Typography variant="body2">{msg.content}</Typography>
                            </Paper>
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', px: 0.5 }}>
                                {format(parseUtc(msg.created_at), 'h:mm a')}
                            </Typography>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Paper 
                component="form" 
                onSubmit={handleSendMessage}
                elevation={3} 
                sx={{ 
                    p: 1.5, 
                    bgcolor: 'white', 
                    borderRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <TextField
                    fullWidth
                    placeholder="Type a message..."
                    size="small"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC'
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small">
                                    <MicIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <IconButton 
                    color="primary" 
                    type="submit" 
                    disabled={!newMessage.trim() || sending}
                    sx={{ 
                        bgcolor: newMessage.trim() ? '#3B82F6' : 'transparent',
                        color: newMessage.trim() ? 'white' : 'inherit',
                        '&:hover': { bgcolor: '#2563EB' }
                    }}
                >
                    {sending ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
            </Paper>
        </Box>
    );
};

export default ConversationView;
